import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './PackagesSection.styles';
import { BusinessTabs } from '@/features/Profile/core';
import { useResponsive } from '@/hooks/useResponsive';
import { BusinessTabName } from '@/features/Profile/core/BusinessTabs/BusinessTabs.types';
import { ActivePackageCard } from '@/features/Profile/core/ActivePackageCard';
import { useCurrentUserPackages } from '@/features/Profile/core/hooks';
import {
  usePackageActivation,
  usePackagesList,
} from '@/features/Profile/sections/Packages/hooks';
import { PackagePurchaseCard } from '@/features/Profile/sections/Packages/components/PackagePurchaseCard';
import { PackageActivationModal } from '@/features/Profile/sections/Packages/components/PackageActivationModal';
import { DiscountPercentIcon } from '@/components/UI/Icons/Icons';
import { SkeletonLoader } from '@/components/SkeletonLoader/SkeletonLoader';

export function PackagesSection() {
  const { isDesktop } = useResponsive();
  const { t } = useTranslation();
  const { packages: activePackages, loading } = useCurrentUserPackages();
  const {
    loading: isPackagesListLoading,
    packageOffers,
    packages,
    periods,
    promotionPeriods,
    selectedPeriod,
    selectPeriod,
  } = usePackagesList();
  const {
    close: closeActivationModal,
    openForPurchase,
    openForRenewal,
    selection: activationSelection,
  } = usePackageActivation(packageOffers, activePackages);

  return (
    <S.Box>
      <S.ActivePackages>
        {!isDesktop && <BusinessTabs activeTab={BusinessTabName.Packages} />}
        <S.Title>{t('pages.cabinet.active_package')}</S.Title>
        {loading && (
          <S.PackagesGrid>
            <SkeletonLoader height={150} width="100%" borderRadius={21} />
          </S.PackagesGrid>
        )}
        {!loading && activePackages.length > 0 && (
          <S.PackagesGrid>
            {activePackages.map(activePackage => (
              <ActivePackageCard
                key={`${activePackage.name}-${activePackage.expiresAt}-${activePackage.price}-${activePackage.walletAmount}`}
                activePackage={activePackage}
                onRenew={openForRenewal}
              />
            ))}
          </S.PackagesGrid>
        )}
        {!loading && activePackages.length === 0 && (
          <S.EmptyMessage>
            {t('pages.cabinet.you_dont_have_active_package')}
          </S.EmptyMessage>
        )}
      </S.ActivePackages>
      <S.PurchasePackages>
        <S.PurchaseHeader>
          <S.Title>{t('pages.cabinet.buy_new_package')}</S.Title>
          {isPackagesListLoading && <S.PeriodsSkeleton aria-hidden="true" />}
          {!isPackagesListLoading && periods.length > 0 && (
            <S.Periods role="group">
              {periods.map(period => (
                <S.PeriodButton
                  key={period}
                  data-stat={`package-period-${period}`}
                  type="button"
                  $isActive={period === selectedPeriod}
                  onClick={() => selectPeriod(period)}
                >
                  {promotionPeriods.includes(period) && (
                    <S.PeriodPromotionBadge
                      aria-hidden="true"
                      data-testid="period-promotion-badge"
                    >
                      <DiscountPercentIcon />
                    </S.PeriodPromotionBadge>
                  )}
                  {t(
                    isDesktop
                      ? 'pages.cabinet.package_period_days'
                      : 'pages.cabinet.package_period_days_short',
                    { days: period }
                  )}
                </S.PeriodButton>
              ))}
            </S.Periods>
          )}
        </S.PurchaseHeader>
        {isPackagesListLoading && (
          <S.PackagesGrid>
            {Array.from({ length: 6 }, (_, index) => (
              <SkeletonLoader
                key={index}
                height={287}
                width="100%"
                borderRadius={21}
              />
            ))}
          </S.PackagesGrid>
        )}
        {!isPackagesListLoading && packages.length > 0 && (
          <S.PackagesGrid>
            {packages.map(item => (
              <PackagePurchaseCard
                key={item.name}
                item={item}
                onConnect={openForPurchase}
              />
            ))}
          </S.PackagesGrid>
        )}
      </S.PurchasePackages>
      {activationSelection && (
        <PackageActivationModal
          {...activationSelection}
          isOpen
          onClose={closeActivationModal}
        />
      )}
    </S.Box>
  );
}
